import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import type { ChangeEvent } from 'react';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { EMPTY_STRING } from '@e-socna/empty';
import { buildMissionDocumentTitle } from '@e-socna/folder-documents';
import type { Id } from '@e-socna/data-layer';

import { getTouchedError } from '../../utils/get-touched-error.util';
import { IdAutocomplete } from '../../components/IdAutocomplete';
import { useFormikOnNextTick } from '../../hooks/useFormikOnNextTick.hook';
import type { IdOption } from '../../components/IdSelect';

import { buildDocumentTemplateOptions } from '../DocumentTemplates/utils/build-document-template-options.util';
import { useDefaultTemplateId } from '../DocumentTemplates/hooks/useDefaultTemplateId.hook';
import type { MissionDocumentTemplate } from '../DocumentTemplates/types/mission-document-template.type';

interface DocumentChoiceValues {
  readonly templateId?: Id | typeof EMPTY_STRING;
  readonly title?: string;
}

interface Props {
  // The template of the mission type, set while none is chosen.
  readonly defaultTemplateId?: Id;
  readonly defaultTitle: string;
  readonly quotationTemplates: readonly MissionDocumentTemplate[];
  readonly suggestedTemplateIds: ReadonlySet<Id>;
}

export const QuotationDocumentChoiceFields = ({
  defaultTemplateId,
  defaultTitle,
  quotationTemplates,
  suggestedTemplateIds,
}: Readonly<Props>) => {
  const { errors, handleBlur, handleChange, setFieldValue, touched, values } =
    useFormikContext<DocumentChoiceValues>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  const [isTitleTouched, setIsTitleTouched] = useState(false);

  useDefaultTemplateId({ defaultTemplateId, templates: quotationTemplates });

  const handleTemplateIdChange = useCallback(
    (id: Id | typeof EMPTY_STRING) => {
      void setFieldValue('templateId', id || EMPTY_STRING);
    },
    [setFieldValue],
  );

  const handleTitleChange = useCallback(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      setIsTitleTouched(true);
      handleChange(event);
    },
    [handleChange],
  );

  const templateOptions = useMemo<readonly IdOption<Id>[]>(
    () =>
      buildDocumentTemplateOptions(quotationTemplates, suggestedTemplateIds),
    [quotationTemplates, suggestedTemplateIds],
  );

  useEffect(() => {
    if (isTitleTouched) {
      return;
    }

    const template = quotationTemplates.find(
      (candidate) => candidate._id === values.templateId,
    );

    setFieldValueOnNextTick(
      'title',
      buildMissionDocumentTitle({
        missionTitle: defaultTitle,
        templateTitle: template?.document.title,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.templateId,
    quotationTemplates,
    defaultTitle,
    setFieldValueOnNextTick,
  ]);

  return (
    <>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <Typography sx={{ mt: 2, mb: 2 }} variant="h6">
          Document du devis
        </Typography>
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <IdAutocomplete<Id, IdOption<Id>>
          label="Modèle (optionnel)"
          name="templateId"
          noOptionsText="Aucun modèle disponible"
          onChange={handleTemplateIdChange}
          options={templateOptions}
          value={values.templateId ?? EMPTY_STRING}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <TextField
          error={!!(errors.title && touched.title)}
          fullWidth
          helperText={getTouchedError(errors, touched, 'title')}
          id="quotation-document-choice-title"
          label="Titre du document"
          name="title"
          onBlur={handleBlur}
          onChange={handleTitleChange}
          required
          type="text"
          value={values.title ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
    </>
  );
};
